import { memo, useCallback, useEffect, useRef } from 'react';
import type { UIMessage } from 'ai';
import { ScrollArea } from '@scio3/ui';
import { Loader2 } from 'lucide-react';
import { cn } from '@scio3/ui';
import type { PageSuggestion } from '../utils/page-suggestions';
import { WelcomeHero } from './WelcomeHero';
import { UserMessage } from './UserMessage';
import { AssistantMessage, TypingIndicator } from './AssistantMessage';
import { BotAvatar } from './BotAvatar';
import { safeToLocaleTime } from '@/lib/safe-format';

function MessageTimestamp({ date, align }: { date?: Date | string; align: 'left' | 'right' }) {
  const time = safeToLocaleTime(date, { hour: '2-digit', minute: '2-digit' });
  if (!time) return null;
  return (
    <p
      className={cn(
        'mt-0.5 text-[10px] text-muted-foreground',
        align === 'right' ? 'text-right' : 'ml-10',
      )}
    >
      {time}
    </p>
  );
}

interface OptiBotMessagesProps {
  messages: UIMessage[];
  isLoading: boolean;
  isLoadingHistory?: boolean;
  onApprove: (toolCallId: string) => void;
  onDeny: (toolCallId: string) => void;
  onFeedback: (messageId: string, score: number, comment?: string) => void;
  suggestions: PageSuggestion[];
  onSuggestionClick: (question: string, suggestionId: string) => void;
  getMessageTimestamp?: (messageId: string) => Date | undefined;
  /** Fires when the list crosses between scrolled-to-top and scrolled-down — drives
   *  the header's scroll shadow (only shown once content slides under the header). */
  onScrolledChange?: (scrolled: boolean) => void;
}

/**
 * Scrollable message list with auto-scroll on new messages.
 * The welcome hero (avatar + greeting + contextual suggestions) shows only on the
 * empty chat; once messages exist it's replaced by the conversation.
 * Wrapped in React.memo to prevent re-renders when only the input state changes.
 * Auto-scroll is paused when the user manually scrolls up, and resumes
 * when a new user message is sent.
 */
export const OptiBotMessages = memo(function OptiBotMessages({
  messages,
  isLoading,
  isLoadingHistory,
  onApprove,
  onDeny,
  onFeedback,
  suggestions,
  onSuggestionClick,
  getMessageTimestamp,
  onScrolledChange,
}: OptiBotMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const prevMessageCountRef = useRef(messages.length);
  // Detect when user scrolls away from the bottom via capture phase on the ScrollArea root.
  // The actual scrolling element is the Radix Viewport child, so we read from event.target.
  const handleScrollCapture = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      // Consider "at bottom" if within 50px of the bottom
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      userScrolledUpRef.current = !isAtBottom;
      // >2px guards against sub-pixel scroll noise at the very top.
      onScrolledChange?.(scrollTop > 2);
    },
    [onScrolledChange],
  );

  // Reset the scroll shadow when the conversation is cleared/empty (no scroll event
  // fires on reset, so the flag would otherwise stay stuck "on").
  useEffect(() => {
    if (messages.length === 0) onScrolledChange?.(false);
  }, [messages.length, onScrolledChange]);

  // Reset userScrolledUp when a new user message is sent
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const lastNewMessage = messages[messages.length - 1];
      if (lastNewMessage?.role === 'user') {
        userScrolledUpRef.current = false;
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive or content streams (unless user scrolled up)
  useEffect(() => {
    if (!userScrolledUpRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  return (
    <ScrollArea
      className="relative flex-1 bg-card"
      thumbClassName="bg-foreground/30 hover:bg-foreground/50"
      onScrollCapture={handleScrollCapture}
    >
      {isLoadingHistory && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/80">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}
      <div className="space-y-3 px-4 py-3">
        {/* Welcome greeting — pinned as the first message so it persists after the
            conversation starts. The suggestion pills show only on the empty chat. */}
        {!isLoadingHistory && (
          <WelcomeHero
            suggestions={suggestions}
            onSuggestionClick={onSuggestionClick}
            showSuggestions={messages.length === 0}
          />
        )}
        {messages.map((message, index) => {
          const isLast = index === messages.length - 1;

          if (message.role === 'user') {
            return (
              <div key={message.id}>
                <UserMessage message={message} />
                <MessageTimestamp date={getMessageTimestamp?.(message.id)} align="right" />
              </div>
            );
          }

          if (message.role === 'assistant') {
            // Hide silently-forwarded messages (empty stream — no text, no tool parts).
            // Still render during streaming so the typing indicator shows briefly.
            const isCurrentlyStreaming = isLast && isLoading;
            const hasVisibleContent = message.parts.some(
              (p) => p.type !== 'text' || (p.type === 'text' && !!(p as { text?: string }).text),
            );
            if (!isCurrentlyStreaming && !hasVisibleContent) return null;

            return (
              <div key={message.id}>
                {/* Timestamp is rendered inside AssistantMessage (right of the feedback
                    row) so the thumbs and time share one line below the bubble. */}
                <AssistantMessage
                  message={message}
                  isLast={isLast}
                  isLoading={isLoading}
                  onApprove={onApprove}
                  onDeny={onDeny}
                  onFeedback={onFeedback}
                  timestamp={getMessageTimestamp?.(message.id)}
                />
              </div>
            );
          }

          return null;
        })}
        {/* Pending indicator: the assistant message doesn't exist in `messages`
            until the stream starts, so show a typing bubble under the user's
            message immediately so they know OptiBot is working. */}
        {isLoading && messages[messages.length - 1]?.role === 'user' && (
          <div className="flex items-start gap-2" data-testid="optibot-pending">
            <BotAvatar />
            <div className="pt-1">
              <TypingIndicator />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
});
